import React, { useState, useEffect } from 'react';
import type { TableWidgetConfig } from './TableWidget';
import type { DataRow } from './ChartDataTable';
import { ChartDataSourceModal } from './ChartDataSourceModal';
import { Database } from 'lucide-react';

interface TableWidgetConfigProps {
  initialConfig: Partial<TableWidgetConfig>;
  onChange: (config: TableWidgetConfig) => void;
}

// Parse data array into DataRows for table input
function parseDataToRows(data?: Array<{ label: string; [key: string]: string | number }>, series?: string[]): DataRow[] {
  if (!data || data.length === 0) {
    const initialRow: DataRow = { label: '' };
    series?.forEach(s => initialRow[s] = '');
    return [initialRow];
  }
  return data.map((d) => {
    const row: DataRow = { label: d.label };
    series?.forEach(s => {
      row[s] = d[s]?.toString() || '';
    });
    return row;
  });
}

// Parse DataRows back into data array
function parseRowsToData(rows: DataRow[], series: string[]): Array<{ label: string; [key: string]: string | number }> {
  return rows
    .filter((row) => row.label)
    .map((row) => {
      const dataPoint: { label: string; [key: string]: string | number } = { label: row.label };
      series.forEach(s => {
        dataPoint[s] = parseFloat(row[s]) || 0;
      });
      return dataPoint;
    });
}

export function TableWidgetConfigForm({ initialConfig = {}, onChange }: TableWidgetConfigProps) {
  const [title, setTitle] = useState(initialConfig?.title || '');
  const [series, setSeries] = useState<string[]>(
    initialConfig?.series && initialConfig.series.length > 0 ? initialConfig.series : ['Value']
  );
  const [seriesColors, setSeriesColors] = useState<Record<string, string>>({});
  const [dataRows, setDataRows] = useState<DataRow[]>(
    parseDataToRows(initialConfig?.data, initialConfig?.series)
  );
  const [showSearch, setShowSearch] = useState<boolean>(
    initialConfig?.showSearch !== undefined ? initialConfig.showSearch : false
  );
  const [rowsPerPage, setRowsPerPage] = useState<number>(initialConfig?.rowsPerPage || 10);
  const [backgroundColor, setBackgroundColor] = useState(initialConfig?.backgroundColor || '');
  const [isDataModalOpen, setIsDataModalOpen] = useState(false);

  // Live update on every change
  useEffect(() => {
    const data = parseRowsToData(dataRows, series);

    const config: TableWidgetConfig = {
      title: title || 'Untitled Table',
      data,
      series,
      showSearch,
      rowsPerPage,
      backgroundColor: backgroundColor || undefined,
    };
    onChange(config);
  }, [title, dataRows, series, showSearch, rowsPerPage, backgroundColor]);

  // Validation
  const hasValidData = dataRows.some((row) => {
    if (!row.label) return false;
    return series.some(s => row[s] && !isNaN(parseFloat(row[s])));
  });

  return (
    <div className="space-y-4 flex-1 overflow-auto">
      {/* Widget Background - Prominent */}
      <div className="bg-gray-50 dark:bg-gray-800 border-2 border-gray-300 dark:border-gray-600 rounded-lg p-3">
        <label className="block text-sm font-semibold text-gray-900 dark:text-white mb-3">
          Widget Background
        </label>
        <div className="flex items-center gap-2">
          <input
            type="color"
            value={backgroundColor || '#ffffff'}
            onChange={(e) => setBackgroundColor(e.target.value)}
            className="w-14 h-14 rounded-lg cursor-pointer border-2 border-gray-400 dark:border-gray-500"
            title="Widget background color"
          />
          <div className="flex-1">
            <input
              type="text"
              value={backgroundColor}
              onChange={(e) => setBackgroundColor(e.target.value)}
              placeholder="#ffffff"
              className="w-full px-3 py-2 text-sm font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white mb-1"
              maxLength={7}
            />
            {backgroundColor && (
              <button
                onClick={() => setBackgroundColor('')}
                className="text-xs text-red-600 dark:text-red-400 hover:underline"
                title="Clear background"
              >
                Reset to default
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Title */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Table Title *
        </label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g., Sales Data"
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
          maxLength={100}
        />
        {!title && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">Title is required</p>
        )}
      </div>

      {/* Data Source Button */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Table Data *
        </label>
        <button
          onClick={() => setIsDataModalOpen(true)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-all"
        >
          <div className="flex items-center gap-2">
            <Database size={20} className="text-purple-600" />
            <div className="text-left">
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                Manual Entry
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {dataRows.length} rows � {series.length} columns
              </div>
            </div>
          </div>
          <div className="text-purple-600 font-semibold text-sm">
            Edit Data �
          </div>
        </button>

        {!hasValidData && (
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            At least one data row required with valid label and number value
          </p>
        )}
      </div>

      {/* Show Search Toggle */}
      <div>
        <label className="flex items-center justify-between cursor-pointer">
          <span className="text-xs font-semibold text-gray-700 dark:text-gray-300">
            Show Search
          </span>
          <div className="relative">
            <input
              type="checkbox"
              checked={showSearch}
              onChange={(e) => setShowSearch(e.target.checked)}
              className="sr-only peer"
            />
            <div className="w-11 h-6 bg-gray-200 dark:bg-gray-700 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-purple-600 dark:peer-focus:ring-purple-400 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-purple-600"></div>
          </div>
        </label>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Enable search box to filter table rows
        </p>
      </div>

      {/* Rows Per Page */}
      <div>
        <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-1">
          Rows Per Page
        </label>
        <select
          value={rowsPerPage}
          onChange={(e) => setRowsPerPage(Number(e.target.value))}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-white text-sm"
        >
          <option value={5}>5 rows</option>
          <option value={10}>10 rows</option>
          <option value={25}>25 rows</option>
          <option value={50}>50 rows</option>
          <option value={100}>100 rows</option>
        </select>
      </div>

      {/* Data Source Modal */}
      <ChartDataSourceModal
        isOpen={isDataModalOpen}
        onClose={() => setIsDataModalOpen(false)}
        rows={dataRows}
        series={series}
        seriesColors={seriesColors}
        onRowsChange={setDataRows}
        onSeriesChange={setSeries}
        onSeriesColorsChange={setSeriesColors}
        onSave={() => {
          setIsDataModalOpen(false);
        }}
      />
    </div>
  );
}
